<template>
  <div class="Pagination">
    <button
      class="prev arrow"
      :disabled="!canGoPrev"
      @click="onClickPrev"
    >
      <PagePrev />
    </button>

    <div class="Pages">
      <button
        v-for="(page, index) of displayedPages"
        :key="index"
        class="PageBtn"
        :class="{active: page === currentPage, dot: typeof page === 'string'}"
        :disabled="typeof page === 'string'"
        @click="() => onClickPage(page)"
      >
        <span class="Page">{{ page }}</span>
      </button>
    </div>

    <button
      class="next arrow"
      :disabled="!canGoNext"
      @click="onClickNext"
    >
      <PageNext />
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { PagePrev, PageNext } from '@ka-nabellinc/ka-nabell-icons'

const MaxPanelSize = 5

export default defineComponent({
  components: {
    PagePrev,
    PageNext,
  },
  props: {
    modelValue: {
      type: Number,
      required: true,
    },
    totalPages: {
      type: Number,
      required: true
    },
    panels: {
      type: Number,
      default: 5,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const divider = '...'

    const currentPage = computed({
      get () {
        return props.modelValue
      },
      set (value) {
        emit('update:modelValue', value)
      }
    })

    const pages = computed(
      () => Array.from({ length: props.totalPages }, (_, i) => i + 1)
    )

    const displayedPages = computed(() => {
      if (pages.value.length <= MaxPanelSize) {
        return pages.value
      } else {
        const current = currentPage.value
        const max = pages.value.length
        const range = Math.floor((Math.min(Math.max(props.panels, MaxPanelSize), max) - 5) / 2)
        const underRange = current - range
        const upperRange = current + range
        const beforeWrapped = underRange - 1 > 1
        const afterWrapped = upperRange + 1 < max
        const items: Array<number | string> = [1]
  
        if (beforeWrapped) items.push(divider)
  
        if (!afterWrapped) {
          const addedItems = (current + range + 2) - max
          for (let i = current - range - addedItems; i <= current - range - 1; i++) {
            items.push(i)
          }
        }
  
        for (let i = underRange > 2 ? (underRange) : 2; i <= Math.min(max, upperRange); i++) {
          items.push(i)
        }
  
        if (!beforeWrapped) {
          const addedItems = 1 - (current - range - 2)
          for (let i = current + range + 1; i <= current + range + addedItems; i++) {
            items.push(i)
          }
        }
  
        if (afterWrapped) items.push(divider)
  
        if (upperRange < max) items.push(max)
  
        if (items.length >= 3 && items[1] === divider && items[2] === 3) {
          items[1] = 2
        }
        if (items.length >= 3 && items[items.length - 2] === divider && items[items.length - 1] === items.length) {
          items[items.length - 2] = items.length - 1
        }
  
        return items
      }
    })

    const canGoPrev = computed(() => currentPage.value > 1)
    const canGoNext = computed(() => currentPage.value < pages.value.length)
  
    function onClickPage (page: number | string) {
      if (typeof page === 'string') {
        return
      }

      currentPage.value = page
    }

    function onClickPrev () {
      if (!canGoPrev.value) {
        return
      }

      currentPage.value--
    }

    function onClickNext () {
      if (!canGoNext.value) {
        return
      }

      currentPage.value++
    }

    return {
      pages,
      currentPage,
      displayedPages,
      canGoPrev,
      canGoNext,
      onClickPage,
      onClickPrev,
      onClickNext,
    }
  }
})
</script>

<style scoped>
.Pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
}

.arrow {
  border: none;
  background: transparent;
  display: flex;
  width: 36px;
  height: 36px;
  padding: 6px;
  cursor: pointer;
  box-sizing: border-box;
}

.arrow:not(:disabled):hover {
  border-radius: 20px;
  background: var(--semantic-colors-background-lightgray);
}

.arrow:disabled {
  cursor: not-allowed;
}

.arrow > * {
  fill: var(--primitive-colors-neutral-black-500);
}

.arrow:hover > * {
  fill: var(--primitive-colors-neutral-black-800);
}

.arrow:disabled > * {
  fill: var(--primitive-colors-neutral-white-800);
}

.Pages {
  display: flex;
  gap: 8px;
}

.PageBtn {
  width: 36px;
  height: 36px;
  padding: 6px 0px 7px 0px;
  border: none;
  box-sizing: border-box;
  background: transparent;
}

.PageBtn:not(.dot) {
  cursor: pointer;
}

.PageBtn:not(.dot):hover {
  border-radius: 20px;
  background: var(--semantic-colors-background-lightgray);
}

.PageBtn.active {
  border-radius: 20px;
  background: var(--semantic-colors-background-gray);
}

.PageBtn .Page {
  color: var(--semantic-colors-text-gray);
  text-align: center;
  font-size: var(--font-size-sub-headline-2);
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0.15px;
}

.PageBtn.active .Page {
  color: var(--semantic-colors-text-black);
}
</style>